import React, { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link } from "react-router-dom";
import ReactPaginate from "react-paginate";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faSpinner,
  faAngleRight,
  faAngleLeft,
  faEllipsis,
} from "@fortawesome/free-solid-svg-icons";
import { debounce } from "lodash";
import { toast } from "react-toastify";
import {
  deleteDdOption,
  listDdOptions,
  getDdOptionForUI,
} from "../../../store/reducer/ddOption/ddOptionSlice";

// Tailwind background color presets
const tailwindColors = [
  { name: "Gray", value: "gray" },
  { name: "Red", value: "red" },
  { name: "Amber", value: "amber" },
  { name: "Yellow", value: "yellow" },
  { name: "Green", value: "green" },
  { name: "Emerald", value: "emerald" },
  { name: "Teal", value: "teal" },
  { name: "Blue", value: "blue" },
  { name: "Indigo", value: "indigo" },
  { name: "Purple", value: "purple" },
  { name: "Pink", value: "pink" },
  { name: "Rose", value: "rose" },
];

function ListDdOption() {
  const dispatch = useDispatch();
  const [search, setSearch] = useState("");
  const [ui_name, setUiName] = useState("");
  const [field_name, setFieldName] = useState("");
  const [optionsForUI, setOptionsForUI] = useState([]);

  const { loading, options = [], totalCount } = useSelector(
    (state) => state.ddOptions
  );
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);

  const handleSearch = (search = "", ui_name = "", field_name = "") => {
    dispatch(listDdOptions({ search, ui_name, field_name, page, limit }));
  };

  const debouncedSearch = useCallback(debounce(handleSearch, 800), [options]);

  const handlePageClick = ({ selected }) => {
    const page = selected + 1;
    setPage(page);
    dispatch(listDdOptions({ search, ui_name, field_name, page, limit }));
  };

  const handleDelete = async (id) => {
    await dispatch(deleteDdOption(id)).then((result) => {
      if (result.payload) {
        dispatch(listDdOptions({ search, ui_name, field_name, page, limit }));
        toast.success(result.payload.message);
      } else {
        toast.error(result.error.message);
      }
    });
  };

  useEffect(() => {
    dispatch(listDdOptions({ search, ui_name, field_name, page, limit }));
    dispatch(getDdOptionForUI()).then((result) => {
      if (result.payload) {
        setOptionsForUI(result.payload);
      }
    });
  }, []);

  return (
    <>
      <div className="block md:flex justify-between mb-[30px]">
        <div className="flex gap-[16px] items-center mb-[32px] md:mb-0">
          <Link to="/setting">
            <div className="bg-[#F8F8F8] rounded-[10px] p-[8px]">
              <img
                className="w-[24px]"
                src={process.env.PUBLIC_URL + "/icons/back.png"}
                alt="back icon"
              />
            </div>
          </Link>
          <div className="flex flex-col">
            <span className="font-Inter font-[400] leading-[16px] text-[#9E9E9E] text-[14px]">
              DropDown / List Option
            </span>
            <span className="font-Inter font-[700] leading-[30px] text-[24px]">
              List DropDown Option
            </span>
          </div>
        </div>
        <div>
          <Link to="/setting/dd-option/add">
            <div className="py-[10px] px-[40px] bg-[#F8F8F8] font-[500] font-Inter text-[18px] flex justify-center text-[#17171B] rounded-[12px] border-2 border-[#17171B] w-max">
              <span>Add Option</span>
            </div>
          </Link>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-col mt-[20px] gap-[40px]">
        <div className="md:p-5 bg-[#F8F8F8] mt-[20px] rounded-l-[12px]">
          <div className="flex flex-col">
            <div className="md:flex justify-between items-center p-[12px]">
              <div className="leading-[30px] font-Mulish font-[700] text-[24px] mb-[8px] md:mb-0">
                <span>Options Data</span>
              </div>
              <div className="flex flex-col md:flex-row gap-[12px]">
                <select
                  className="block w-full md:w-[200px] bg-white border border-gray-200 text-gray-700 py-3 px-4 rounded-[12px]"
                  value={ui_name}
                  onChange={(e) => {
                    setUiName(e.target.value);
                    setFieldName("");
                    debouncedSearch(search, e.target.value, "");
                  }}
                >
                  <option value="">Select UI Name</option>
                  {[...new Set(optionsForUI.map((opt) => opt.ui_name))].map(
                    (ui) => (
                      <option key={ui} value={ui}>
                        {ui}
                      </option>
                    )
                  )}
                </select>

                <select
                  className="block w-full md:w-[200px] bg-white border border-gray-200 text-gray-700 py-3 px-4 rounded-[12px]"
                  value={field_name}
                  onChange={(e) => {
                    setFieldName(e.target.value);
                    debouncedSearch(search, ui_name, e.target.value);
                  }}
                  disabled={!ui_name}
                >
                  <option value="">Select Field Name</option>
                  {ui_name &&
                    [
                      ...new Set(
                        optionsForUI
                          .filter((o) => o.ui_name === ui_name)
                          .map((o) => o.field_name)
                      ),
                    ].map((field) => (
                      <option key={field} value={field}>
                        {field}
                      </option>
                    ))}
                </select>

                <div className="relative">
                  <input
                    className="block appearance-none w-full bg-white border border-gray-200 text-gray-700 py-3 px-4 pl-[60px] rounded-[12px]"
                    type="text"
                    id="search"
                    name="search"
                    placeholder="Search..."
                    onChange={(e) => {
                      setSearch(e.target.value);
                      debouncedSearch(e.target.value, ui_name, field_name);
                    }}
                  />
                  <div className="pointer-events-none absolute inset-y-0 flex items-center px-5 text-gray-700">
                    <img
                      src={process.env.PUBLIC_URL + "/icons/search.png"}
                      alt=""
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Table */}
            <div className="sm:rounded-lg mt-5 overflow-scroll">
              <table className="w-full text-center">
                <thead className="bg-white">
                  <tr className="rounded-[12px]">
                    {[
                      "Id",
                      "UI Name",
                      "Field Name",
                      "Field Code",
                      "Field Value",
                      "Description",
                      "Is Default",
                      "Row Color",
                      "Options",
                    ].map((header) => (
                      <th
                        key={header}
                        className="px-6 py-3 font-[Inter] font-[600] text-[14px] text-[#6D6D6F]"
                      >
                        {header}
                      </th>
                    ))}
                  </tr>
                </thead>

                {loading ? (
                  <tbody>
                    <tr>
                      <td colSpan={9} className="py-6 text-center">
                        <FontAwesomeIcon icon={faSpinner} size="2xl" spin />
                      </td>
                    </tr>
                  </tbody>
                ) : (
                  <tbody>
                    {options.map((option) => (
                      <tr
                        key={option.id}
                        className={`text-center`}
                      >
                        <td className="px-6 py-4 text-[14px] text-[#6D6D6F] font-[600]">
                          {option.id}
                        </td>
                        <td className="px-6 py-4 text-[14px] text-[#6D6D6F]">
                          {option.ui_name}
                        </td>
                        <td className="px-6 py-4 text-[14px] text-[#6D6D6F]">
                          {option.field_name}
                        </td>
                        <td className="px-6 py-4 text-[14px] text-[#6D6D6F]">
                          {option.field_code}
                        </td>
                        <td className="px-6 py-4 text-[14px] text-[#6D6D6F]">
                          {option.field_value}
                        </td>
                        <td className="px-6 py-4 text-[14px] text-[#6D6D6F]">
                          {option.field_desc}
                        </td>
                        <td className="px-6 py-4">
                          <span
                            className={`p-1 inline-flex text-xs leading-5 font-semibold rounded-lg ${
                              option.is_default
                                ? "bg-green-200 text-green-800"
                                : "bg-red-200 text-red-800"
                            }`}
                          >
                            {option.is_default ? "Yes" : "No"}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-[14px] text-[#6D6D6F] capitalize">
                          {option.field_color || "-"}
                        </td>
                        <td className="px-3 py-2">
                          <div className="flex gap-[10px] justify-center">
                            <Link to={`/setting/dd-option/${option.id}`}>
                              <img
                                src={
                                  process.env.PUBLIC_URL + "/icons/edit.png"
                                }
                                alt="edit"
                                className="w-[40px] bg-teal-100 rounded-lg"
                              />
                            </Link>
                            <span
                              className="cursor-pointer"
                              onClick={() => handleDelete(option.id)}
                            >
                              <img
                                src={
                                  process.env.PUBLIC_URL + "/icons/delete.png"
                                }
                                alt="delete"
                                className="w-[40px] bg-red-100 rounded-lg"
                              />
                            </span>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                )}
              </table>
            </div>
          </div>
        </div>
      </div>

      {/* Pagination */}
      <div className="flex justify-between items-center mt-[20px] gap-[40px] md:p-5 bg-[#F8F8F8] rounded-l-[12px]">
        <div className="hidden md:block font-Inter font-[300] text-nowrap">
          <span>
            Showing <b>{page}</b> of <b>{Math.ceil(totalCount / limit)}</b> pages
          </span>
        </div>
        <div className="w-full flex justify-end">
          <ReactPaginate
            className="react_pagination_ul"
            breakLabel={
              <span className="w-10 h-10 flex justify-center items-center bg-white rounded-md mr-4">
                <FontAwesomeIcon icon={faEllipsis} size="xl" />
              </span>
            }
            nextLabel={
              <span className="w-10 h-10 flex justify-center items-center bg-white rounded-md">
                <FontAwesomeIcon icon={faAngleRight} size="xl" />
              </span>
            }
            onPageChange={handlePageClick}
            pageCount={Math.ceil(totalCount / limit)}
            previousLabel={
              <span className="w-10 h-10 flex justify-center items-center bg-white rounded-md mr-4">
                <FontAwesomeIcon icon={faAngleLeft} size="xl" />
              </span>
            }
            activeClassName="bg-black text-white"
            pageClassName="block hover:bg-black hover:text-white rounded-md  mr-4"
            pageLinkClassName="w-10 h-10 flex justify-center items-center"
            containerClassName="flex items-center justify-center"
          />
        </div>
      </div>
    </>
  );
}

export default ListDdOption;
